import { Navigate, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { cn } from "@/lib/utils";

export function StatCard({ label, value, sub, icon: Icon, tone = "default", className, children }) {
  const tones = {
    default: "text-ink",
    good: "text-stock-in",
    warn: "text-stock-low",
    bad: "text-stock-out",
    brand: "text-brand",
  };
  return (
    <div className={cn("panel panel-pad animate-rise", className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="eyebrow">{label}</div>
          <div className={cn("stat-figure mt-2", tones[tone])}>{value}</div>
          {sub && <div className="mt-1.5 text-[12.5px] text-muted2">{sub}</div>}
          {children}
        </div>
        {Icon && (
          <div className="w-9 h-9 rounded-xl bg-brand-light flex items-center justify-center shrink-0">
            <Icon className="w-[18px] h-[18px] text-brand" />
          </div>
        )}
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      {Icon && (
        <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center mb-4">
          <Icon className="w-5 h-5 text-brand" />
        </div>
      )}
      <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
      {body && <p className="mt-1.5 text-sm text-muted2 max-w-sm">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn("w-5 h-5 animate-spin text-brand", className)} />;
}

export function LoadingPanel({ rows = 5 }) {
  return (
    <div className="panel panel-pad space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-10 rounded-lg bg-line/50 animate-pulse"
          style={{ animationDelay: `${i * 60}ms` }}
        />
      ))}
    </div>
  );
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <Spinner className="w-6 h-6" />
    </div>
  );
}

/**
 * Signed in, and working inside a client.
 *
 * The fallback differs by role on purpose: sending a client admin to /overview
 * would bounce them to /dashboard, which would bounce them back here — an
 * infinite redirect that renders a blank page.
 */
export function ProtectedRoute() {
  const { user, loading } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant();

  if (loading || user === null || tenantLoading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  // A temporary password must be replaced before anything else.
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (!tenantId) {
    return <Navigate to={user.role === "owner" ? "/overview" : "/login"} replace />;
  }
  return <Outlet />;
}

/** Settings and Team — the owner, or an admin of the current client. */
export function AdminRoute() {
  const { user, loading } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant();

  if (loading || user === null || tenantLoading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  // A temporary password must be replaced before anything else.
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (!tenantId) {
    return <Navigate to={user.role === "owner" ? "/overview" : "/login"} replace />;
  }
  if (!["owner", "admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

/** Signed in, no client selection needed — used by Profile. */
export function SignedInRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  // A temporary password must be replaced before anything else.
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  return <Outlet />;
}

/** Owner-only screens — overview, clients, platform settings. */
export function OwnerRoute() {
  const { user, loading } = useAuth();

  if (loading || user === null) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  // A temporary password must be replaced before anything else.
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (user.role !== "owner") return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}
